import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { SandboxItemApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(SandboxItemApi);

export const useSandboxItems = () => {
  const queryClient = useQueryClient();

  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const useCreateSandboxItem = () =>
    useMutation({
      mutationFn: (
        body: Parameters<
          typeof apiClient.sandboxItemsControllerCreateSandboxItem
        >["0"]["sandboxItemCreateCommand"]
      ) =>
        apiClient.sandboxItemsControllerCreateSandboxItem({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          sandboxItemCreateCommand: body,
        }),
    });

  const usePatchSandboxItem = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemId: string;
        sandboxItemUpdateCommand: Parameters<
          typeof apiClient.sandboxItemsControllerUpdateSandboxItem
        >["0"]["sandboxItemUpdateCommand"];
      }) =>
        apiClient.sandboxItemsControllerUpdateSandboxItem({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          sandboxItemUpdateCommand: params.sandboxItemUpdateCommand,
          sandboxItemId: params.sandboxItemId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useDeleteSandboxItemVersionFile = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemVersionId: string;
      }) =>
        apiClient.sandboxItemsControllerDeleteSandboxItemVersionFile({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          sandboxItemVersionId: params.sandboxItemVersionId,
        }),

      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]);
      },
    });

  return {
    useCreateSandboxItem,
    usePatchSandboxItem,
    useDeleteSandboxItemVersionFile,
  };
};
